import type { Approver } from "../../../../types/types.Workflow";
import { toTitleCase } from "../../../../util/util";

export const APPROVER_IDENTITY_FIELDS = new Set([
  "id",
  "username",
  "firstname",
  "lastname",
  "full_name",
  "email_address",
]);

export const STAGE_APPROVER_IDENTITY_FIELDS = new Set([
  "approver_id",
  "approver_name",
  "approver_email",
  "approver_status",
  "approver_comment",
  "approver_decision",
]);

export function getApproverExtraFields(
  rows: Array<object> | undefined,
  identityFields: Set<string>
): string[] {
  if (!rows?.length) {
    return [];
  }

  const fields: string[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    for (const field of Object.keys(row)) {
      if (identityFields.has(field) || seen.has(field)) {
        continue;
      }
      seen.add(field);
      fields.push(field);
    }
  }

  return fields;
}

export function formatApproverAttributeValue(value: unknown): string {
  if (typeof value === "string" && value.trim() !== "") {
    return value;
  }
  return "-";
}

export type ApproverStageSection = {
  key: string;
  title: string;
  approvers: Approver[];
};

/**
 * Config stages in sequence. Prefer `stageOrder` because JSON key order
 * is not reliable.
 */
export function getApproverStages(
  approversByStage: Record<string, Approver[]> | undefined,
  stageOrder?: string[],
  stageNames?: Record<string, string>
): ApproverStageSection[] {
  if (!approversByStage) {
    return [];
  }

  const seen = new Set<string>();
  const keys: string[] = [];
  for (const key of stageOrder ?? []) {
    if (key in approversByStage && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  for (const key of Object.keys(approversByStage)) {
    if (!seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }

  return keys.map((key) => ({
    key,
    title: stageNames?.[key] || toTitleCase(key.replace(/_/g, " ")),
    approvers: approversByStage[key] ?? [],
  }));
}
